import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Video, Plus, Edit2, Trash2, Search, 
  ChevronRight, ChevronDown, Save, X, 
  CheckCircle, AlertCircle, Loader2, Lock, LogOut,
  Upload, Cloud, Grid, List,
  FolderOpen, Eye, Move, LayoutGrid,
  Home, Info, Heart, Coffee, Users, BookOpen,
  Play, Image, ImagePlus, Camera, Palette,
  Sparkles, Building, Globe, Handshake, Lightbulb,
  Menu, ChevronLeft, Mail, BarChart3, Scissors, MessageCircle,
  ArrowUpDown, ChevronUp, GripVertical, Crop
} from 'lucide-react';
import { DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import VideoTrimmer from '../components/VideoTrimmer';
import ImageCropModal from '../components/admin/ImageCropModal';
import QuickMoveModal from '../components/admin/QuickMoveModal';
import PreordersPanel from '../components/admin/PreordersPanel';
import SubscribersPanel from '../components/admin/SubscribersPanel';
import AnalyticsPanel from '../components/admin/AnalyticsPanel';
import QuestionsPanel from '../components/admin/QuestionsPanel';
import { 
  getVideos, 
  getVideoCategories, 
  createVideo, 
  updateVideo, 
  deleteVideo,
  getCloudflareImageUploadUrl,
  importVideoFromUrl,
  proxyVideoUpload,
  getImages,
  getImageCategories,
  createImage,
  updateImage,
  deleteImage,
  updateCategoryCover,
  getCoffeePreorders,
  getEmailSubscribers,
  getAnalyticsSummary,
  getPageAnalytics,
  cropImage
} from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL;


// Sortable wrapper for drag-and-drop reorder
const SortableVideoCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

// Section icons mapping
const SECTION_ICONS = {
  'Media Page': BookOpen,
  'New Cloudflare Uploads': Cloud,
  'Homepage': Home,
  'About Page': Info,
  'Programs Page': Users,
  'Coffee Page': Coffee,
  'Donate Page': Heart,
  'Get Involved Page': Users,
  'Children Photos': Camera,
};

// Category icons for covers
const CATEGORY_ICONS = {
  bible_tutoring: BookOpen,
  children_stories_vlogs: Heart,
  gods_love_action: Heart,
  serving_food: Coffee,
  joyful_moments_bdays: Sparkles,
  local_outreach_events: Globe,
  volunteers_partners_action: Handshake,
  fun_activities: Users,
  needs_construction: Building,
  thank_you_shoutouts: Lightbulb
};

// Default cover images
const DEFAULT_COVERS = {
  bible_tutoring: 'https://images.unsplash.com/photo-1504052434569-70ad5836ab65?w=600&q=80',
  children_stories_vlogs: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?w=600&q=80',
  gods_love_action: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=600&q=80',
  serving_food: 'https://images.unsplash.com/photo-1547592180-85f173990554?w=600&q=80',
  joyful_moments_bdays: 'https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=600&q=80',
  local_outreach_events: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?w=600&q=80',
  volunteers_partners_action: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?w=600&q=80',
  fun_activities: 'https://images.unsplash.com/photo-1544776193-352d25ca82cd?w=600&q=80',
  needs_construction: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=600&q=80',
  thank_you_shoutouts: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80'
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    setTimeout(() => {
      if (password === 'THEnewwebsite2026') {
        sessionStorage.setItem('admin_authenticated', 'true');
        onLogin();
      } else {
        setError('Incorrect password');
      }
      setLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Media Manager</h1>
          <p className="text-gray-600 mt-2">Enter password to continue</p>
        </div>

        <form onSubmit={handleSubmit}>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter admin password"
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none mb-4"
            autoFocus
            data-testid="admin-password-input"
          />
          {error && (
            <p className="mb-4 text-sm text-red-500 flex items-center gap-1">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={loading || !password}
            className="w-full py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="admin-login-btn"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-5 h-5" />}
            {loading ? 'Verifying...' : 'Access Panel'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  useEffect(() => {
    if (sessionStorage.getItem('admin_authenticated') === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  if (!isAuthenticated) {
    return <AdminLogin onLogin={() => setIsAuthenticated(true)} />;
  }

  return <AdminDashboard onLogout={() => {
    sessionStorage.removeItem('admin_authenticated');
    setIsAuthenticated(false);
  }} />;
};

const AdminDashboard = ({ onLogout }) => {
  // Active tab: 'videos', 'images', or 'covers'
  const [activeTab, setActiveTab] = useState('videos');
  
  // Video State
  const [videos, setVideos] = useState([]);
  const [videoCategories, setVideoCategories] = useState([]);
  const [videoCategoriesBySection, setVideoCategoriesBySection] = useState({});
  
  // Image State
  const [images, setImages] = useState([]);
  const [imageCategories, setImageCategories] = useState([]);
  const [imageCategoriesBySection, setImageCategoriesBySection] = useState({});
  
  // Covers State
  const [categoryCovers, setCategoryCovers] = useState({});
  const [storiesCategories, setStoriesCategories] = useState([]);
  
  // Pre-orders State
  const [preorders, setPreorders] = useState([]);
  
  // Subscribers State
  const [subscribers, setSubscribers] = useState([]);
  const [questions, setQuestions] = useState([]);
  
  // Analytics State
  const [analyticsSummary, setAnalyticsSummary] = useState(null);
  const [pageAnalytics, setPageAnalytics] = useState([]);
  const [analyticsTimeRange, setAnalyticsTimeRange] = useState(30);
  
  // Common State
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSection, setSelectedSection] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [expandedSections, setExpandedSections] = useState({});
  const [notification, setNotification] = useState(null);
  const [reorderMode, setReorderMode] = useState(false);
  
  // Form state
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Upload state
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadedId, setUploadedId] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);
  const [editingCover, setEditingCover] = useState(null);

  // Quick move state
  const [movingItem, setMovingItem] = useState(null);
  const [croppingItem, setCroppingItem] = useState(null);
  
  // Mobile sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Video trimmer state
  const [trimmingVideo, setTrimmingVideo] = useState(null);
  const [playingVideoId, setPlayingVideoId] = useState(null);

  // Fetch data on mount and tab change
  const fetchData = useCallback(async (resetSelection = false) => {
    setLoading(true);
    try {
      if (activeTab === 'videos') {
        const [videosData, categoriesData] = await Promise.all([
          getVideos(),
          getVideoCategories()
        ]);
        setVideos(videosData);
        setVideoCategories(categoriesData.categories || []);
        setVideoCategoriesBySection(categoriesData.by_section || {});
        
        const expanded = {};
        Object.keys(categoriesData.by_section || {}).forEach(section => {
          expanded[section] = true;
        });
        setExpandedSections(expanded);
      } else if (activeTab === 'images') {
        const [imagesData, categoriesData] = await Promise.all([
          getImages(),
          getImageCategories()
        ]);
        setImages(imagesData);
        setImageCategories(categoriesData.categories || []);
        setImageCategoriesBySection(categoriesData.by_section || {});
        
        const expanded = {};
        Object.keys(categoriesData.by_section || {}).forEach(section => {
          expanded[section] = true;
        });
        setExpandedSections(expanded);
      } else if (activeTab === 'covers') {
        const categoriesData = await getVideoCategories();
        // Get Stories & Media categories
        const storiesCats = categoriesData.by_section?.['Media Page'] || [];
        setStoriesCategories(storiesCats);
        
        // Build covers map
        const covers = {};
        categoriesData.categories?.forEach(cat => {
          if (cat.cover_image) {
            covers[cat.value] = cat.cover_image;
          }
        });
        setCategoryCovers(covers);
      } else if (activeTab === 'preorders') {
        const preordersData = await getCoffeePreorders();
        setPreorders(preordersData);
      } else if (activeTab === 'subscribers') {
        const subscribersData = await getEmailSubscribers();
        setSubscribers(subscribersData);
      } else if (activeTab === 'questions') {
        const res = await fetch(`${API}/api/questions`);
        const data = await res.json();
        setQuestions(data);
      } else if (activeTab === 'analytics') {
        const [summaryData, pagesData] = await Promise.all([
          getAnalyticsSummary(analyticsTimeRange),
          getPageAnalytics(analyticsTimeRange)
        ]);
        setAnalyticsSummary(summaryData);
        setPageAnalytics(pagesData.pages || []);
      }
      if (resetSelection) {
        setSelectedSection('all');
        setSelectedCategory('all');
        setSearchQuery('');
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      showNotification('Failed to load data', 'error');
    }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, analyticsTimeRange]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Get current categories based on tab
  const categories = activeTab === 'videos' ? videoCategories : imageCategories;
  const categoriesBySection = activeTab === 'videos' ? videoCategoriesBySection : imageCategoriesBySection;
  const items = activeTab === 'videos' ? videos : images;

  // Filter items
  const filteredItems = items.filter(item => {
    const matchesSearch = !searchQuery || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesSection = selectedSection === 'all' || 
      Object.entries(categoriesBySection).some(([section, cats]) => 
        section === selectedSection && cats.some(c => c.value === item.category)
      );
    
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    
    return matchesSearch && matchesSection && matchesCategory;
  }).sort((a, b) => (a.display_order || 999) - (b.display_order || 999));

  // Get category label
  const getCategoryLabel = (value) => {
    const cat = categories.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  // Toggle section expansion
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Form handlers
  const openAddForm = (preselectedCategory = '') => {
    setEditingItem(null);
    if (activeTab === 'videos') {
      setFormData({
        title: '',
        cloudflare_uid: '',
        source: 'cloudflare',
        category: preselectedCategory || 'unpublished_1',
        description: '',
        thumbnail_url: '',
        featured: false
      });
    } else {
      setFormData({
        title: '',
        url: '',
        cloudflare_id: '',
        category: preselectedCategory || 'img_unpublished_1',
        description: '',
        alt_text: ''
      });
    }
    setFormErrors({});
    setUploadedId(null);
    setUploadProgress(0);
    setUploadStatus(null);
    setIsFormOpen(true);
  };

  const openEditForm = (item) => {
    setEditingItem(item);
    if (activeTab === 'videos') {
      setFormData({
        title: item.title,
        cloudflare_uid: item.cloudflare_uid || '',
        source: item.source || 'cloudflare',
        category: item.category,
        description: item.description || '',
        thumbnail_url: item.thumbnail_url || '',
        featured: item.featured || false
      });
      setUploadedId(item.cloudflare_uid || null);
    } else {
      setFormData({
        title: item.title,
        url: item.url || '',
        cloudflare_id: item.cloudflare_id || '',
        category: item.category,
        description: item.description || '',
        alt_text: item.alt_text || ''
      });
      setUploadedId(item.cloudflare_id || null);
    }
    setFormErrors({});
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingItem(null);
    setUploadedId(null);
    setUploadProgress(0);
    setUploadStatus(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  // Video upload handler - uses proxy upload through backend for better mobile compatibility
  const handleVideoUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Uploading video through server...');
    
    try {
      // Use proxy upload through backend - works better on mobile
      const result = await proxyVideoUpload(file, (progress) => {
        setUploadProgress(progress);
      });
      
      if (result.success && result.uid) {
        setUploadedId(result.uid);
        setFormData(prev => ({ ...prev, cloudflare_uid: result.uid }));
        setUploadStatus('Upload complete! Processing...');
        showNotification('Video uploaded successfully!');
      } else {
        throw new Error(result.message || 'Upload failed');
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('Upload failed: ' + (error.response?.data?.detail || error.message), 'error');
      setUploadStatus('Upload failed');
      setUploadedId(null);
    } finally {
      setUploading(false);
    }
  };

  // Image upload handler
  const handleImageUpload = async (file) => {
    if (!file) return;
    
    setUploading(true);
    setUploadProgress(0);
    setUploadStatus('Getting upload URL...');
    
    try {
      const { uploadURL, id } = await getCloudflareImageUploadUrl();
      if (!uploadURL) throw new Error('Failed to get upload URL');
      
      setUploadStatus('Uploading image...');
      
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        });
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              if (response.result && response.result.variants) {
                const publicUrl = response.result.variants[0];
                setFormData(prev => ({ ...prev, url: publicUrl, cloudflare_id: id }));
                setUploadedId(id);
              }
              resolve(response);
            } catch (e) {
              resolve(xhr.response);
            }
          } else {
            reject(new Error(`Upload failed: ${xhr.status}`));
          }
        });
        xhr.addEventListener('error', () => reject(new Error('Upload failed')));
        xhr.open('POST', uploadURL);
        xhr.send(formDataUpload);
      });
      
      setUploadStatus('Upload complete!');
      showNotification('Image uploaded successfully!');
      
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('Upload failed: ' + error.message, 'error');
      setUploadStatus('Upload failed');
      setUploadedId(null);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const errors = {};
    if (!formData.title.trim()) errors.title = 'Title is required';
    if (!formData.category) errors.category = 'Category is required';
    
    if (activeTab === 'videos') {
      if (formData.source === 'cloudflare' && !formData.cloudflare_uid && !uploadedId) {
        errors.cloudflare_uid = 'Please upload a video';
      }
    } else {
      if (!formData.url && !uploadedId) {
        errors.url = 'Please upload an image or enter a URL';
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSaving(true);
    try {
      if (activeTab === 'videos') {
        const videoData = {
          title: formData.title.trim(),
          cloudflare_uid: formData.source === 'cloudflare' ? (uploadedId || formData.cloudflare_uid) : null,
          source: formData.source,
          category: formData.category,
          description: formData.description.trim() || null,
          thumbnail_url: formData.thumbnail_url.trim() || null,
          featured: formData.featured
        };

        if (editingItem) {
          await updateVideo(editingItem.id, videoData);
          showNotification('Video updated!');
        } else {
          await createVideo(videoData);
          showNotification('Video added!');
        }
      } else {
        const imageData = {
          title: formData.title.trim(),
          url: formData.url,
          cloudflare_id: uploadedId || formData.cloudflare_id || null,
          category: formData.category,
          description: formData.description.trim() || null,
          alt_text: formData.alt_text.trim() || null
        };

        if (editingItem) {
          await updateImage(editingItem.id, imageData);
          showNotification('Image updated!');
        } else {
          await createImage(imageData);
          showNotification('Image added!');
        }
      }

      await fetchData();
      closeForm();
    } catch (error) {
      console.error('Error saving:', error);
      showNotification('Failed to save', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;

    try {
      if (activeTab === 'videos') {
        await deleteVideo(item.id);
      } else {
        await deleteImage(item.id);
      }
      showNotification('Deleted successfully');
      await fetchData();
    } catch (error) {
      showNotification('Failed to delete', 'error');
    }
  };



  // Reorder via drag-and-drop
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 5 } })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = filteredItems.findIndex(v => v.id === active.id);
    const newIndex = filteredItems.findIndex(v => v.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(filteredItems, oldIndex, newIndex);
    const videoIds = reordered.map(v => v.id);

    try {
      await fetch(`${API}/api/videos/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_ids: videoIds })
      });
      await fetchData();
      showNotification('Order updated');
    } catch (err) {
      showNotification('Failed to reorder', 'error');
    }
  };

  // Move a video up or down within the current category (mobile-friendly)
  const handleMoveInOrder = async (itemId, direction) => {
    const idx = filteredItems.findIndex(v => v.id === itemId);
    if (idx === -1) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= filteredItems.length) return;

    const reordered = arrayMove(filteredItems, idx, newIdx);
    const videoIds = reordered.map(v => v.id);

    try {
      await fetch(`${API}/api/videos/reorder`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ video_ids: videoIds })
      });
      await fetchData();
      showNotification(`Moved ${direction === 'up' ? 'up' : 'down'}`);
    } catch (err) {
      showNotification('Failed to reorder', 'error');
    }
  };


  const handleQuickMove = async (item, newCategory) => {
    try {
      if (activeTab === 'videos') {
        await updateVideo(item.id, { ...item, category: newCategory });
      } else {
        await updateImage(item.id, { ...item, category: newCategory });
      }
      showNotification(`Moved to ${getCategoryLabel(newCategory)}`);
      setMovingItem(null);
      await fetchData();
    } catch (error) {
      showNotification('Failed to move', 'error');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex" data-testid="admin-page">
      {/* Notification */}
      <AnimatePresence>
        {notification && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 left-1/2 -translate-x-1/2 md:left-auto md:translate-x-0 md:right-4 z-[60] px-4 py-3 rounded-xl shadow-lg flex items-center gap-2 text-sm ${
              notification.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
            }`}
          >
            {notification.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <CheckCircle className="w-5 h-5" />}
            {notification.message}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Sidebar Overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Left Sidebar */}
      <div className={`
        fixed md:sticky top-0 left-0 z-50 md:z-auto
        w-72 bg-white border-r border-slate-200 flex flex-col h-screen
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        {/* Header with Tabs */}
        <div className="p-4 border-b border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-slate-800">Media</h1>
            <div className="flex items-center gap-1">
              {/* Close sidebar on mobile */}
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg md:hidden"
                title="Close menu"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={onLogout}
                className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                title="Logout"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
          
          {/* Tab Switcher */}
          <div className="grid grid-cols-3 gap-1 bg-slate-100 rounded-lg p-1 mb-4">
            <button
              onClick={() => setActiveTab('videos')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'videos' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-videos"
            >
              <Video className="w-3.5 h-3.5" />
              Videos
            </button>
            <button
              onClick={() => setActiveTab('images')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'images' 
                  ? 'bg-white text-green-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-images"
            >
              <Image className="w-3.5 h-3.5" />
              Images
            </button>
            <button
              onClick={() => setActiveTab('covers')}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'covers' 
                  ? 'bg-white text-purple-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-covers"
            >
              <Palette className="w-3.5 h-3.5" />
              Covers
            </button>
            <button
              onClick={() => { setActiveTab('preorders'); setSidebarOpen(false); }}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'preorders' 
                  ? 'bg-white text-amber-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-preorders"
            >
              <Coffee className="w-3.5 h-3.5" />
              Orders
            </button>
            <button
              onClick={() => { setActiveTab('subscribers'); setSidebarOpen(false); }}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'subscribers' 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-subscribers"
            >
              <Mail className="w-3.5 h-3.5" />
              Emails
            </button>
            <button
              onClick={() => { setActiveTab('analytics'); setSidebarOpen(false); }}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'analytics' 
                  ? 'bg-white text-emerald-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-analytics"
            >
              <BarChart3 className="w-3.5 h-3.5" />
              Stats
            </button>
            <button
              onClick={() => { setActiveTab('questions'); setSidebarOpen(false); }}
              className={`flex items-center justify-center gap-1 px-2 py-2 rounded-md text-xs font-medium transition-all ${
                activeTab === 'questions' 
                  ? 'bg-white text-orange-600 shadow-sm' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid="tab-questions"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              Questions
            </button>
          </div>
          
          {/* Search - hide on covers tab */}
          {activeTab !== 'covers' && activeTab !== 'preorders' && activeTab !== 'subscribers' && activeTab !== 'analytics' && activeTab !== 'questions' && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={`Search ${activeTab}...`}
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="admin-search-input"
              />
            </div>
          )}
        </div>

        {/* Category Tree - hide on covers and preorders tab */}
        {activeTab !== 'covers' && activeTab !== 'preorders' && activeTab !== 'subscribers' && activeTab !== 'analytics' && activeTab !== 'questions' && (
        <div className="flex-1 overflow-y-auto p-2">
          {/* All Items */}
          <button
            onClick={() => { setSelectedSection('all'); setSelectedCategory('all'); setReorderMode(false); setSidebarOpen(false); }}
            className={`w-full flex items-center gap-2 px-3 py-2.5 md:py-2 rounded-lg text-left text-sm font-medium mb-2 ${
              selectedSection === 'all' && selectedCategory === 'all'
                ? activeTab === 'videos' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                : 'text-slate-600 hover:bg-slate-100 active:bg-slate-200'
            }`}
          >
            <LayoutGrid className="w-4 h-4" />
            All {activeTab === 'videos' ? 'Videos' : 'Images'}
            <span className="ml-auto text-xs bg-slate-200 px-2 py-0.5 rounded-full">
              {items.length}
            </span>
          </button>

          {/* Sections */}
          {Object.entries(categoriesBySection).map(([section, cats]) => {
            const SectionIcon = SECTION_ICONS[section] || FolderOpen;
            const isExpanded = expandedSections[section];
            const sectionItems = items.filter(v => cats.some(c => c.value === v.category));
            const isUnpublished = section.includes('Unpublished');
            
            return (
              <div key={section} className="mb-1">
                <button
                  onClick={() => toggleSection(section)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 md:py-2 rounded-lg text-left text-sm font-medium ${
                    isUnpublished ? 'text-orange-600' : 'text-slate-700'
                  } hover:bg-slate-100 active:bg-slate-200`}
                >
                  {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  <SectionIcon className="w-4 h-4" />
                  <span className="flex-1 truncate">{section}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    isUnpublished ? 'bg-orange-100 text-orange-600' : 'bg-slate-200 text-slate-600'
                  }`}>
                    {sectionItems.length}
                  </span>
                </button>
                
                {isExpanded && (
                  <div className="ml-4 mt-1 space-y-0.5">
                    {cats.map(cat => {
                      const catItems = items.filter(v => v.category === cat.value);
                      return (
                        <button
                          key={cat.value}
                          onClick={() => { setSelectedSection(section); setSelectedCategory(cat.value); if (section !== 'Media Page') setReorderMode(false); setSidebarOpen(false); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 md:py-1.5 rounded-lg text-left text-xs ${
                            selectedCategory === cat.value
                              ? activeTab === 'videos' ? 'bg-blue-100 text-blue-700 font-medium' : 'bg-green-100 text-green-700 font-medium'
                              : 'text-slate-500 hover:bg-slate-100 active:bg-slate-200'
                          }`}
                        >
                          <span className="flex-1 truncate">{cat.label}</span>
                          {catItems.length > 0 && (
                            <span className="text-xs bg-slate-200 px-1.5 py-0.5 rounded">
                              {catItems.length}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        )}

        {/* Covers Tab - Sidebar Info */}
        {activeTab === 'covers' && (
          <div className="flex-1 overflow-y-auto p-4">
            <div className="text-center mb-6">
              <Palette className="w-12 h-12 text-purple-500 mx-auto mb-3" />
              <h3 className="font-bold text-slate-800">Section Covers</h3>
              <p className="text-sm text-slate-500 mt-1">
                Customize the cover images for each video category on the Media page
              </p>
            </div>
            <div className="bg-purple-50 rounded-xl p-4 text-sm text-purple-700">
              <p className="font-medium mb-2">How it works:</p>
              <ul className="space-y-1 text-purple-600">
                <li>• Click "Change" on any card</li>
                <li>• Upload a new image</li>
                <li>• Or paste an image URL</li>
              </ul>
            </div>
          </div>
        )}

        {/* Add Button - hide on covers and preorders tab */}
        {activeTab !== 'covers' && activeTab !== 'preorders' && activeTab !== 'subscribers' && (
        <div className="p-4 border-t border-slate-200">
          <button
            onClick={() => { openAddForm(); setSidebarOpen(false); }}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 text-white rounded-xl font-semibold transition-colors ${
              activeTab === 'videos' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
            }`}
            data-testid="add-video-btn"
          >
            {activeTab === 'videos' ? <Upload className="w-5 h-5" /> : <ImagePlus className="w-5 h-5" />}
            {activeTab === 'videos' ? 'Upload Video' : 'Upload Image'}
          </button>
        </div>
        )}
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0 md:ml-0">
        {/* Mobile Header */}
        <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between md:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 -ml-2 text-slate-600 hover:bg-slate-100 rounded-lg"
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-lg font-bold text-slate-800">
            {activeTab === 'covers' ? 'Covers' : activeTab === 'preorders' ? 'Pre-orders' : activeTab === 'subscribers' ? 'Subscribers' : activeTab === 'analytics' ? 'Analytics' : activeTab === 'questions' ? 'Have Questions' : activeTab === 'videos' ? 'Videos' : 'Images'}
          </h1>
          <button
            onClick={() => { openAddForm(); }}
            className={`p-2 -mr-2 rounded-lg ${
              activeTab === 'videos' ? 'text-blue-600 hover:bg-blue-50' : activeTab === 'images' ? 'text-green-600 hover:bg-green-50' : 'text-purple-600 hover:bg-purple-50'
            }`}
            data-testid="mobile-add-btn"
          >
            {activeTab !== 'covers' && activeTab !== 'preorders' && activeTab !== 'subscribers' && activeTab !== 'questions' && <Plus className="w-6 h-6" />}
          </button>
        </div>
        
        <div className="p-4 md:p-6">
        {/* Header - Videos/Images tabs */}
        {activeTab !== 'covers' && activeTab !== 'preorders' && activeTab !== 'subscribers' && activeTab !== 'analytics' && activeTab !== 'questions' && (
        <>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-slate-800">
              {selectedCategory !== 'all' ? getCategoryLabel(selectedCategory) : 
               selectedSection !== 'all' ? selectedSection : `All ${activeTab === 'videos' ? 'Videos' : 'Images'}`}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {filteredItems.length} {activeTab === 'videos' ? 'video' : 'image'}{filteredItems.length !== 1 ? 's' : ''}
              {searchQuery && ` matching "${searchQuery}"`}
            </p>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Reorder toggle - only when viewing one of the 10 media categories */}
            {activeTab === 'videos' && selectedSection === 'Media Page' && selectedCategory !== 'all' && (
              <button
                onClick={() => setReorderMode(!reorderMode)}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  reorderMode 
                    ? 'bg-amber-100 text-amber-700 border border-amber-300' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200 border border-slate-200'
                }`}
                data-testid="reorder-toggle"
              >
                <ArrowUpDown className="w-4 h-4" />
                <span className="hidden sm:inline">{reorderMode ? 'Done' : 'Reorder'}</span>
              </button>
            )}
            {/* Mobile search */}
            <div className="relative flex-1 sm:hidden">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex bg-white rounded-lg border border-slate-200 p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? (activeTab === 'videos' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600') : 'text-slate-400 hover:text-slate-600'}`}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded ${viewMode === 'list' ? (activeTab === 'videos' ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600') : 'text-slate-400 hover:text-slate-600'}`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Grid/List View */}
        {filteredItems.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
            {activeTab === 'videos' ? <Video className="w-16 h-16 mx-auto mb-4 text-slate-300" /> : <Image className="w-16 h-16 mx-auto mb-4 text-slate-300" />}
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No {activeTab} found</h3>
            <p className="text-slate-500 mb-6">
              {searchQuery ? 'Try a different search' : `Upload your first ${activeTab === 'videos' ? 'video' : 'image'}`}
            </p>
            <button
              onClick={() => openAddForm(selectedCategory !== 'all' ? selectedCategory : '')}
              className={`inline-flex items-center gap-2 px-6 py-3 text-white rounded-xl font-semibold ${
                activeTab === 'videos' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <Upload className="w-5 h-5" />
              Upload {activeTab === 'videos' ? 'Video' : 'Image'}
            </button>
          </div>
        ) : viewMode === 'grid' ? (
          reorderMode && activeTab === 'videos' && selectedSection === 'Media Page' && selectedCategory !== 'all' ? (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={filteredItems.map(i => i.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
                {filteredItems.map((item, idx) => (
                  <SortableVideoCard key={item.id} id={item.id}>
                    <div
                      className="bg-white rounded-xl shadow-sm overflow-hidden border-2 border-amber-400"
                      data-testid={`reorder-card-${item.id}`}
                    >
                      <div className="relative aspect-video bg-slate-200">
                        <img 
                          src={item.thumbnail_url || ''} 
                          alt={item.title}
                          className="w-full h-full object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                        {/* Order badge */}
                        <div className="absolute top-2 left-2 bg-amber-400 text-slate-950 text-sm font-bold w-8 h-8 rounded-full flex items-center justify-center shadow">
                          {idx + 1}
                        </div>
                        {/* Drag handle - desktop */}
                        <div className="absolute top-2 right-2 hidden md:flex bg-black/60 rounded-lg p-1.5 cursor-grab active:cursor-grabbing">
                          <GripVertical className="w-5 h-5 text-white" />
                        </div>
                      </div>
                      <div className="p-2 flex items-center justify-between gap-1">
                        <p className="font-medium text-slate-800 text-xs truncate flex-1">{item.title}</p>
                        <div className="flex gap-1 shrink-0">
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMoveInOrder(item.id, 'up'); }}
                            disabled={idx === 0}
                            className={`p-1.5 rounded-lg transition-colors ${idx === 0 ? 'text-slate-300' : 'text-slate-600 hover:bg-blue-100 hover:text-blue-600 active:bg-blue-200'}`}
                            data-testid={`move-up-${item.id}`}
                          >
                            <ChevronUp className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => { e.stopPropagation(); handleMoveInOrder(item.id, 'down'); }}
                            disabled={idx === filteredItems.length - 1}
                            className={`p-1.5 rounded-lg transition-colors ${idx === filteredItems.length - 1 ? 'text-slate-300' : 'text-slate-600 hover:bg-blue-100 hover:text-blue-600 active:bg-blue-200'}`}
                            data-testid={`move-down-${item.id}`}
                          >
                            <ChevronDown className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </SortableVideoCard>
                ))}
              </div>
            </SortableContext>
          </DndContext>
          ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
            {filteredItems.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl shadow-sm overflow-hidden group hover:shadow-md transition-shadow"
                data-testid={`${activeTab}-card-${item.id}`}
              >
                <div className={`relative ${activeTab === 'videos' ? 'aspect-video' : 'aspect-square'} bg-slate-200`}>
                  {activeTab === 'videos' && playingVideoId === item.id ? (
                    <>
                      {item.source === 'direct' && item.direct_url ? (
                        <video
                          src={item.direct_url}
                          className="w-full h-full object-contain bg-black"
                          controls
                          autoPlay
                          playsInline
                        />
                      ) : item.cloudflare_uid ? (
                        <iframe
                          src={`https://customer-vbbn7p39z8nnfjwk.cloudflarestream.com/${item.cloudflare_uid}/iframe?autoplay=true`}
                          className="w-full h-full"
                          allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                          allowFullScreen
                        />
                      ) : null}
                      <button
                        onClick={(e) => { e.stopPropagation(); setPlayingVideoId(null); }}
                        className="absolute top-1.5 right-1.5 p-1 bg-black/70 rounded-full text-white hover:bg-red-600 z-10"
                        title="Stop"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </>
                  ) : activeTab === 'videos' ? (
                    <>
                      <img 
                        src={item.thumbnail_url || ''} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="absolute inset-0 flex items-center justify-center text-slate-400">
                        <Play className="w-6 h-6 md:w-8 md:h-8" />
                      </div>
                      <div className={`absolute top-1.5 left-1.5 md:top-2 md:left-2 px-1.5 md:px-2 py-0.5 rounded text-[9px] md:text-[10px] font-bold ${
                        item.source === 'cloudflare' ? 'bg-orange-500 text-white' : item.source === 'placeholder' ? 'bg-slate-500 text-white' : 'bg-green-500 text-white'
                      }`}>
                        {item.source === 'cloudflare' ? 'CF' : item.source === 'placeholder' ? 'EMPTY' : item.source === 'direct' ? 'HD' : item.source?.toUpperCase() || '?'}
                      </div>
                      {/* Duration badge */}
                      {item.duration && (
                        <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 px-1.5 md:px-2 py-0.5 rounded bg-black/80 text-white text-[10px] md:text-xs font-medium">
                          {item.duration}
                        </div>
                      )}
                    </>
                  ) : (
                    <img 
                      src={item.url} 
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { 
                        e.target.style.display = 'none'; 
                        if (e.target.nextSibling) {
                          e.target.nextSibling.style.display = 'flex';
                        }
                      }}
                    />
                  )}
                  <div className="absolute inset-0 items-center justify-center text-slate-400 hidden">
                    {activeTab === 'videos' ? <Play className="w-6 h-6 md:w-8 md:h-8" /> : <Image className="w-6 h-6 md:w-8 md:h-8" />}
                  </div>
                  
                  {item.featured && (
                    <div className="absolute top-1.5 right-1.5 md:top-2 md:right-2 px-1.5 md:px-2 py-0.5 rounded text-[9px] md:text-[10px] font-bold bg-yellow-400 text-yellow-900">
                      FEATURED
                    </div>
                  )}

                  {/* Reorder drag handle overlay - only shown in non-DnD grid as fallback */}
                  
                  {/* Desktop hover overlay */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity hidden md:flex flex-col items-center justify-center gap-2">
                    {/* Play button - main action */}
                    {activeTab === 'videos' && (
                      <button
                        onClick={() => setPlayingVideoId(item.id)}
                        className="p-4 bg-white rounded-full text-slate-700 hover:bg-green-100 hover:text-green-600 mb-2"
                        title="Play Video"
                      >
                        <Play className="w-6 h-6" fill="currentColor" />
                      </button>
                    )}
                    {/* Action buttons row */}
                    <div className="flex items-center gap-2">
                      {activeTab === 'videos' && item.cloudflare_uid && (
                        <button
                          onClick={() => setTrimmingVideo(item)}
                          className="p-2 bg-white rounded-lg text-slate-700 hover:bg-purple-100"
                          title="Trim Video"
                        >
                          <Scissors className="w-4 h-4" />
                        </button>
                      )}
                      {activeTab === 'images' && (
                        <button
                          onClick={() => setCroppingItem(item)}
                          className="p-2 bg-white rounded-lg text-slate-700 hover:bg-purple-100"
                          title="Crop Image"
                        >
                          <Crop className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => openEditForm(item)}
                        className="p-2 bg-white rounded-lg text-slate-700 hover:bg-blue-100"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setMovingItem(item)}
                        className="p-2 bg-white rounded-lg text-slate-700 hover:bg-green-100"
                        title="Move to..."
                      >
                        <Move className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(item)}
                        className="p-2 bg-white rounded-lg text-slate-700 hover:bg-red-100"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="p-2 md:p-3">
                  <h4 className="font-medium text-slate-800 text-xs md:text-sm truncate" title={item.title}>
                    {item.title}
                  </h4>
                  <p className="text-[10px] md:text-xs text-slate-500 truncate mt-0.5 md:mt-1">
                    {getCategoryLabel(item.category)}
                  </p>
                  {/* Mobile action buttons */}
                  <div className="flex items-center gap-1 mt-2 md:hidden">
                    {activeTab === 'videos' && (
                      <button
                        onClick={() => setPlayingVideoId(item.id)}
                        className="p-1.5 text-green-600 bg-green-50 rounded-lg active:bg-green-100"
                        title="Play"
                      >
                        <Play className="w-3.5 h-3.5" fill="currentColor" />
                      </button>
                    )}
                    <button
                      onClick={() => openEditForm(item)}
                      className="flex-1 flex items-center justify-center gap-1 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg active:bg-blue-100"
                    >
                      <Edit2 className="w-3 h-3" />
                      Edit
                    </button>
                    {activeTab === 'videos' && item.cloudflare_uid && (
                      <button
                        onClick={() => setTrimmingVideo(item)}
                        className="p-1.5 text-purple-500 bg-purple-50 rounded-lg active:bg-purple-100"
                        title="Trim"
                      >
                        <Scissors className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {activeTab === 'images' && (
                      <button
                        onClick={() => setCroppingItem(item)}
                        className="p-1.5 text-purple-500 bg-purple-50 rounded-lg active:bg-purple-100"
                        title="Crop"
                      >
                        <Crop className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button
                      onClick={() => setMovingItem(item)}
                      className="p-1.5 text-slate-500 bg-slate-50 rounded-lg active:bg-slate-100"
                    >
                      <Move className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => handleDelete(item)}
                      className="p-1.5 text-red-500 bg-red-50 rounded-lg active:bg-red-100"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          )
        ) : (
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">{activeTab === 'videos' ? 'Video' : 'Image'}</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600">Category</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className={`${activeTab === 'videos' ? 'w-20 h-12' : 'w-12 h-12'} bg-slate-200 rounded overflow-hidden flex-shrink-0`}>
                          {activeTab === 'videos' ? (
                            <img 
                              src={item.thumbnail_url || ''} 
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : (
                            <img 
                              src={item.url} 
                              alt=""
                              className="w-full h-full object-cover"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          )}
                        </div>
                        <p className="font-medium text-slate-800 text-sm">{item.title}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">
                        {getCategoryLabel(item.category)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEditForm(item)} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Edit2 className="w-4 h-4" /></button>
                        {activeTab === 'images' && (
                          <button onClick={() => setCroppingItem(item)} className="p-2 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg" title="Crop"><Crop className="w-4 h-4" /></button>
                        )}
                        <button onClick={() => setMovingItem(item)} className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg"><Move className="w-4 h-4" /></button>
                        <button onClick={() => handleDelete(item)} className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </>
        )}

        {/* Covers Tab Content */}
        {activeTab === 'covers' && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">Section Cover Images</h2>
              <p className="text-slate-500 text-sm mt-1">
                Customize the cover images displayed on the Media page
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {storiesCategories.map((category) => {
                const Icon = CATEGORY_ICONS[category.value] || Heart;
                const currentCover = categoryCovers[category.value] || DEFAULT_COVERS[category.value];
                const isEditing = editingCover === category.value;
                
                return (
                  <div 
                    key={category.value}
                    className="bg-white rounded-xl md:rounded-2xl shadow-sm overflow-hidden"
                  >
                    {/* Cover Image Preview */}
                    <div className="relative h-32 md:h-40 bg-slate-200">
                      <img 
                        src={currentCover}
                        alt={category.label}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.src = DEFAULT_COVERS[category.value]; }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-2 md:bottom-3 left-2 md:left-3 right-2 md:right-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 md:w-8 md:h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                            <Icon className="w-3.5 h-3.5 md:w-4 md:h-4 text-white" />
                          </div>
                          <h3 className="text-white font-bold text-xs md:text-sm line-clamp-1">{category.label}</h3>
                        </div>
                      </div>
                    </div>
                    
                    {/* Actions */}
                    <div className="p-3 md:p-4">
                      {isEditing ? (
                        <div className="space-y-2 md:space-y-3">
                          <input
                            type="text"
                            placeholder="Paste image URL..."
                            className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter' && e.target.value) {
                                try {
                                  await updateCategoryCover(category.value, e.target.value);
                                  setCategoryCovers(prev => ({ ...prev, [category.value]: e.target.value }));
                                  showNotification('Cover updated!');
                                  setEditingCover(null);
                                } catch (error) {
                                  showNotification('Failed to update cover', 'error');
                                }
                              }
                            }}
                            data-testid={`cover-url-${category.value}`}
                          />
                          <div className="flex gap-2">
                            <button
                              onClick={() => coverFileInputRef.current?.click()}
                              className="flex-1 flex items-center justify-center gap-1 px-3 py-2.5 bg-purple-100 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-200 active:bg-purple-200"
                            >
                              <Upload className="w-4 h-4" />
                              Upload
                            </button>
                            <button
                              onClick={() => setEditingCover(null)}
                              className="px-3 py-2.5 bg-slate-100 text-slate-600 rounded-lg text-sm font-medium hover:bg-slate-200 active:bg-slate-200"
                            >
                              Cancel
                            </button>
                          </div>
                          <input
                            ref={coverFileInputRef}
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files[0];
                              if (!file) return;
                              
                              setUploading(true);
                              setUploadStatus('Uploading...');
                              
                              try {
                                const { uploadURL, id } = await getCloudflareImageUploadUrl();
                                if (!uploadURL) throw new Error('Failed to get upload URL');
                                
                                const formDataUpload = new FormData();
                                formDataUpload.append('file', file);
                                
                                const response = await fetch(uploadURL, {
                                  method: 'POST',
                                  body: formDataUpload
                                });
                                
                                if (response.ok) {
                                  const data = await response.json();
                                  if (data.result?.variants?.[0]) {
                                    const imageUrl = data.result.variants[0];
                                    await updateCategoryCover(category.value, imageUrl);
                                    setCategoryCovers(prev => ({ ...prev, [category.value]: imageUrl }));
                                    showNotification('Cover updated!');
                                    setEditingCover(null);
                                  }
                                } else {
                                  throw new Error('Upload failed');
                                }
                              } catch (error) {
                                console.error('Upload error:', error);
                                showNotification('Upload failed: ' + error.message, 'error');
                              } finally {
                                setUploading(false);
                                setUploadStatus(null);
                              }
                            }}
                          />
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-slate-500">
                            {categoryCovers[category.value] ? 'Custom image' : 'Default image'}
                          </span>
                          <button
                            onClick={() => setEditingCover(category.value)}
                            className="flex items-center gap-1 px-3 py-1.5 bg-purple-100 text-purple-600 rounded-lg text-sm font-medium hover:bg-purple-200"
                            data-testid={`change-cover-${category.value}`}
                          >
                            <Edit2 className="w-3.5 h-3.5" />
                            Change
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Reset All Button */}
            <div className="mt-8 text-center">
              <button
                onClick={async () => {
                  if (window.confirm('Reset all covers to default images?')) {
                    // This would require a backend endpoint to delete all covers
                    showNotification('Cover images use defaults when no custom image is set');
                  }
                }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Reset all to defaults
              </button>
            </div>
          </div>
        )}

        {/* Pre-orders Panel */}
        {activeTab === 'preorders' && (
          <PreordersPanel
            preorders={preorders}
            setPreorders={setPreorders}
            showNotification={showNotification}
          />
        )}

        {/* Subscribers Panel */}
        {activeTab === 'subscribers' && (
          <SubscribersPanel
            subscribers={subscribers}
            setSubscribers={setSubscribers}
            showNotification={showNotification}
          />
        )}

        {/* Analytics Panel */}
        {activeTab === 'analytics' && (
          <AnalyticsPanel
            analyticsSummary={analyticsSummary}
            pageAnalytics={pageAnalytics}
            analyticsTimeRange={analyticsTimeRange}
            setAnalyticsTimeRange={setAnalyticsTimeRange}
            onRefresh={fetchData}
          />
        )}
        {/* Questions Panel */}
        {activeTab === 'questions' && (
          <QuestionsPanel
            questions={questions}
            setQuestions={setQuestions}
            showNotification={showNotification}
          />
        )}

        </div>
      </div>

      {/* Quick Move Modal */}
      <QuickMoveModal
        movingItem={movingItem}
        activeTab={activeTab}
        categoriesBySection={categoriesBySection}
        onMove={handleQuickMove}
        onClose={() => setMovingItem(null)}
      />

      {/* Crop Modal */}
      <ImageCropModal
        croppingItem={croppingItem}
        onClose={() => setCroppingItem(null)}
        onCropComplete={() => {
          setCroppingItem(null);
          fetchData();
        }}
        showNotification={showNotification}
      />



      {/* Add/Edit Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 md:p-4"
          >
            <motion.div
              initial={{ y: '100%', opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: '100%', opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="bg-white rounded-t-2xl md:rounded-2xl shadow-xl w-full md:max-w-xl max-h-[95vh] md:max-h-[90vh] overflow-y-auto"
              data-testid="form-modal"
            >
              <div className="p-4 md:p-5 border-b flex items-center justify-between sticky top-0 bg-white z-10">
                <h2 className="text-lg md:text-xl font-bold text-slate-800">
                  {editingItem ? `Edit ${activeTab === 'videos' ? 'Video' : 'Image'}` : `Upload ${activeTab === 'videos' ? 'Video' : 'Image'}`}
                </h2>
                <button onClick={closeForm} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 md:p-5 space-y-4 md:space-y-5">
                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title || ''}
                    onChange={handleInputChange}
                    placeholder={activeTab === 'videos' ? 'Video title' : 'Image title'}
                    className={`w-full px-4 py-3 md:py-2.5 text-base md:text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      formErrors.title ? 'border-red-400' : 'border-slate-200'
                    }`}
                    data-testid="form-title-input"
                  />
                  {formErrors.title && <p className="mt-1 text-xs text-red-500">{formErrors.title}</p>}
                </div>

                {/* Video-specific fields */}
                {activeTab === 'videos' && (
                  <>
                    {/* Source Selection */}
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Video Source (Choose One)</label>
                      <div className="grid grid-cols-1 gap-2">
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, source: 'import' }))}
                          className={`p-4 border-2 rounded-xl flex items-center gap-3 transition-all ${
                            formData.source === 'import' ? 'border-green-500 bg-green-50' : 'border-green-300 bg-green-50/50 hover:border-green-400'
                          }`}
                          data-testid="source-import-btn"
                        >
                          <Cloud className={`w-6 h-6 ${formData.source === 'import' ? 'text-green-600' : 'text-green-500'}`} />
                          <div className="text-left">
                            <span className={`text-sm font-bold ${formData.source === 'import' ? 'text-green-700' : 'text-green-600'}`}>From URL (Mobile Friendly)</span>
                            <p className="text-xs text-green-600">Upload via Google Drive link</p>
                          </div>
                        </button>
                        <button
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, source: 'cloudflare' }))}
                          className={`p-4 border-2 rounded-xl flex items-center gap-3 transition-all ${
                            formData.source === 'cloudflare' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 hover:border-slate-300'
                          }`}
                          data-testid="source-cloudflare-btn"
                        >
                          <Upload className={`w-6 h-6 ${formData.source === 'cloudflare' ? 'text-orange-500' : 'text-slate-400'}`} />
                          <div className="text-left">
                            <span className={`text-sm font-medium ${formData.source === 'cloudflare' ? 'text-orange-600' : 'text-slate-600'}`}>Direct Upload</span>
                            <p className="text-xs text-slate-500">Upload from this device</p>
                          </div>
                        </button>
                      </div>
                    </div>

                    {/* Import from URL (Google Drive, Dropbox) */}
                    {formData.source === 'import' && (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                          <h4 className="font-bold text-green-800 mb-2">📱 Mobile Upload via URL</h4>
                          <ol className="text-sm text-green-700 space-y-1">
                            <li>1. Upload video to Google Drive or Dropbox from your phone</li>
                            <li>2. Get the shareable link (set to "Anyone with link")</li>
                            <li>3. Paste the link below</li>
                          </ol>
                        </div>
                        
                        {uploadedId ? (
                          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                            <div className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <div className="flex-1">
                                <p className="font-medium text-green-700 text-sm">Video Imported!</p>
                                <p className="text-xs text-green-600">Processing on Cloudflare...</p>
                              </div>
                            </div>
                          </div>
                        ) : uploading ? (
                          <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl text-center">
                            <Loader2 className="w-10 h-10 mx-auto mb-2 text-green-500 animate-spin" />
                            <p className="text-sm font-medium text-slate-600">{uploadStatus}</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <input
                              type="url"
                              placeholder="Paste Google Drive or Dropbox link here..."
                              className="w-full px-4 py-3 text-base border-2 border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500"
                              id="import-url-input"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                const urlInput = document.getElementById('import-url-input');
                                const url = urlInput?.value;
                                if (!url) {
                                  showNotification('Please paste a URL first', 'error');
                                  return;
                                }
                                if (!formData.title) {
                                  showNotification('Please enter a title first', 'error');
                                  return;
                                }
                                setUploading(true);
                                setUploadStatus('Importing video from URL...');
                                try {
                                  const result = await importVideoFromUrl(url, formData.title);
                                  if (result.success) {
                                    setUploadedId(result.uid);
                                    setFormData(prev => ({ ...prev, cloudflare_uid: result.uid, source: 'cloudflare' }));
                                    setUploadStatus(result.message);
                                    showNotification('Video imported successfully!');
                                  }
                                } catch (error) {
                                  console.error('Import error:', error);
                                  showNotification(error.response?.data?.detail || 'Failed to import video', 'error');
                                } finally {
                                  setUploading(false);
                                }
                              }}
                              className="w-full py-4 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white font-bold rounded-xl flex items-center justify-center gap-2"
                            >
                              <Cloud className="w-5 h-5" />
                              Import Video to Cloudflare
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cloudflare Upload */}
                    {formData.source === 'cloudflare' && (
                      <div>
                        {uploadedId ? (
                          <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                            <div className="flex items-center gap-3">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <div className="flex-1">
                                <p className="font-medium text-green-700 text-sm">Video Uploaded</p>
                                {uploadStatus && <p className="text-xs text-green-600">{uploadStatus}</p>}
                              </div>
                              <button type="button" onClick={() => { setUploadedId(null); setFormData(prev => ({ ...prev, cloudflare_uid: '' })); }} className="text-green-600 hover:text-green-800 p-1">
                                <X className="w-5 h-5" />
                              </button>
                            </div>
                          </div>
                        ) : uploading ? (
                          <div className="p-6 md:p-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl text-center">
                            <Loader2 className="w-10 h-10 mx-auto mb-2 text-blue-500 animate-spin" />
                            <p className="text-sm font-medium text-slate-600">{uploadStatus}</p>
                            <div className="mt-3 w-full bg-slate-200 rounded-full h-3">
                              <div className="bg-blue-500 h-3 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            <p className="text-sm text-slate-500 mt-2 font-medium">{uploadProgress}%</p>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <p className="text-sm font-medium text-slate-700 text-center">Select a video from your device:</p>
                            
                            {/* Fully visible file input - styled */}
                            <input 
                              type="file" 
                              accept="video/*"
                              onChange={(e) => {
                                if (e.target.files && e.target.files[0]) {
                                  handleVideoUpload(e.target.files[0]);
                                }
                              }}
                              className="block w-full text-sm text-slate-500
                                file:mr-4 file:py-4 file:px-6
                                file:rounded-xl file:border-0
                                file:text-base file:font-bold
                                file:bg-blue-600 file:text-white
                                hover:file:bg-blue-700
                                active:file:bg-blue-800
                                file:cursor-pointer
                                cursor-pointer
                                border-2 border-dashed border-slate-300 rounded-xl p-4"
                            />
                            
                            <p className="text-xs text-slate-400 text-center">MP4, MOV up to 2GB</p>
                          </div>
                        )}
                        {formErrors.cloudflare_uid && <p className="mt-1 text-xs text-red-500">{formErrors.cloudflare_uid}</p>}
                      </div>
                    )}
                  </>
                )}

                {/* Image-specific fields */}
                {activeTab === 'images' && (
                  <div>
                    {uploadedId || formData.url ? (
                      <div className="space-y-3">
                        {formData.url && (
                          <div className="relative w-full h-40 md:h-48 bg-slate-100 rounded-xl overflow-hidden">
                            <img src={formData.url} alt="Preview" className="w-full h-full object-contain" />
                          </div>
                        )}
                        <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                          <CheckCircle className="w-5 h-5 text-green-500" />
                          <p className="font-medium text-green-700 text-sm flex-1">Image uploaded</p>
                          <button type="button" onClick={() => { setUploadedId(null); setFormData(prev => ({ ...prev, url: '', cloudflare_id: '' })); }} className="text-green-600 hover:text-green-800 p-1">
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ) : uploading ? (
                      <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl text-center">
                        <Loader2 className="w-10 h-10 mx-auto mb-2 text-green-500 animate-spin" />
                        <p className="text-sm font-medium text-slate-600">{uploadStatus}</p>
                        <div className="mt-3 w-full bg-slate-200 rounded-full h-3">
                          <div className="bg-green-500 h-3 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
                        </div>
                        <p className="text-sm text-slate-500 mt-2 font-medium">{uploadProgress}%</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className="text-sm font-medium text-slate-700 text-center">Select an image from your device:</p>
                        
                        {/* Fully visible file input - styled */}
                        <input 
                          type="file" 
                          accept="image/*"
                          onChange={(e) => {
                            if (e.target.files && e.target.files[0]) {
                              handleImageUpload(e.target.files[0]);
                            }
                          }}
                          className="block w-full text-sm text-slate-500
                            file:mr-4 file:py-4 file:px-6
                            file:rounded-xl file:border-0
                            file:text-base file:font-bold
                            file:bg-green-600 file:text-white
                            hover:file:bg-green-700
                            active:file:bg-green-800
                            file:cursor-pointer
                            cursor-pointer
                            border-2 border-dashed border-slate-300 rounded-xl p-4"
                        />
                        
                        <p className="text-xs text-slate-400 text-center">JPG, PNG, GIF, WebP</p>
                      </div>
                    )}
                    {formErrors.url && <p className="mt-1 text-xs text-red-500">{formErrors.url}</p>}
                    
                    {/* Manual URL option */}
                    <div className="mt-3">
                      <details className="text-sm">
                        <summary className="cursor-pointer text-slate-500 hover:text-slate-700">Or enter image URL manually</summary>
                        <input
                          type="text"
                          name="url"
                          value={formData.url || ''}
                          onChange={handleInputChange}
                          placeholder="https://example.com/image.jpg"
                          className="w-full mt-2 px-4 py-3 md:py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-green-500 text-base md:text-sm"
                        />
                      </details>
                    </div>
                  </div>
                )}

                {/* Category */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Place on Website <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category || ''}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 md:py-2.5 text-base md:text-sm border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${formErrors.category ? 'border-red-400' : 'border-slate-200'}`}
                    data-testid="form-category-select"
                  >
                    <option value="">Select location...</option>
                    {Object.entries(categoriesBySection).map(([section, cats]) => (
                      <optgroup key={section} label={section}>
                        {cats.map(cat => (
                          <option key={cat.value} value={cat.value}>{cat.label}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                  {formErrors.category && <p className="mt-1 text-xs text-red-500">{formErrors.category}</p>}
                  <p className="mt-1 text-xs text-slate-500">Choose "Unpublished" to save without showing on website</p>
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    value={formData.description || ''}
                    onChange={handleInputChange}
                    placeholder="Brief description (optional)"
                    rows="2"
                    className="w-full px-4 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Alt text for images */}
                {activeTab === 'images' && (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Alt Text</label>
                    <input
                      type="text"
                      name="alt_text"
                      value={formData.alt_text || ''}
                      onChange={handleInputChange}
                      placeholder="Describe the image for accessibility"
                      className="w-full px-4 py-3 md:py-2.5 text-base md:text-sm border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                )}

                {/* Featured (videos only) */}
                {activeTab === 'videos' && (
                  <label className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg active:bg-slate-50">
                    <input
                      type="checkbox"
                      name="featured"
                      checked={formData.featured || false}
                      onChange={handleInputChange}
                      className="w-6 h-6 md:w-5 md:h-5 text-blue-600 border-slate-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-base md:text-sm font-medium text-slate-700">Mark as Featured</span>
                  </label>
                )}

                {/* Actions */}
                <div className="flex items-center gap-3 pt-4 md:pt-2 sticky bottom-0 bg-white pb-2 -mb-2">
                  <button type="button" onClick={closeForm} className="flex-1 py-3.5 md:py-3 text-slate-600 hover:bg-slate-100 active:bg-slate-200 rounded-xl font-medium text-base md:text-sm">
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className={`flex-1 py-3.5 md:py-3 text-white rounded-xl font-semibold disabled:opacity-50 flex items-center justify-center gap-2 text-base md:text-sm ${
                      activeTab === 'videos' ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' : 'bg-green-600 hover:bg-green-700 active:bg-green-800'
                    }`}
                    data-testid="form-submit-btn"
                  >
                    {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
                    {saving ? 'Saving...' : editingItem ? 'Update' : 'Save'}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Video Trimmer Modal */}
      {trimmingVideo && (
        <VideoTrimmer
          videoUrl={trimmingVideo.cloudflare_uid 
            ? `https://customer-vbbn7p39z8nnfjwk.cloudflarestream.com/${trimmingVideo.cloudflare_uid}/downloads/default.mp4`
            : trimmingVideo.direct_url || trimmingVideo.url
          }
          videoTitle={trimmingVideo.title}
          videoId={trimmingVideo.id}
          cloudflareUid={trimmingVideo.cloudflare_uid}
          category={trimmingVideo.category}
          onTrimComplete={(_, newVideo) => {
            showNotification('Trimmed video saved successfully!');
            fetchData();
            setTrimmingVideo(null);
          }}
          onClose={() => setTrimmingVideo(null)}
        />
      )}
    </div>
  );
};

export default AdminPage;
